import { Typography } from "@mui/material";
import { orderBy } from "lodash";
import { Fragment, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import type { DatasetMetricResponse } from "@citrine/resources/metrics";
import { hasDatasetData } from "@citrine/resources/metrics";

import { useDataVizColorVariants } from "#src/hooks/use-data-viz-colors";

import { ActivityTypeCard } from "../activity-type-card";

import { DatasetActivityCard } from "./dataset-activity-card";
import { DatasetActivityLink } from "./dataset-activity-link";
import * as sx from "./dataset-dashboard-card.sx";
import { MaterialCountPieChart } from "./material-count-pie-chart";
import { NoDatasetsMessage } from "./no-datasets-message";

export function DatasetDashboardCard({
  className,
  metrics,
}: {
  className?: string;
  metrics: DatasetMetricResponse;
}) {
  return (
    <sx.CardGrid className={className}>
      <Typography variant="overline">
        <FormattedMessage id="resources.dataset.activity-list.title" />
      </Typography>
      <sx.SubTitle variant="caption">
        <FormattedMessage id="resources.dataset.activity-list.subtitle" />
      </sx.SubTitle>

      <Typography
        variant="overline"
        sx={{
          gridColumn: "2 / span 2",
        }}
      >
        <FormattedMessage id="resources.dataset.material-count-list.title" />
      </Typography>
      <sx.SubTitle variant="caption">
        <FormattedMessage id="resources.dataset.material-count-list.subtitle" />
      </sx.SubTitle>

      <sx.PlotHeader variant="caption">
        <FormattedMessage id="resources.dataset.material-count-plot.subtitle" />
      </sx.PlotHeader>

      {hasDatasetData(metrics) ? (
        <DatasetDashboardCardView metrics={metrics} />
      ) : (
        <NoDatasetsMessage />
      )}
    </sx.CardGrid>
  );
}

function DatasetDashboardCardView({
  metrics,
}: {
  metrics: DatasetMetricResponse;
}) {
  const { sortedDatasetCounts, topFiveDatasetCounts, recentlyUpdated } =
    useDashboardCardData(metrics);

  const colors = useDataVizColorVariants();

  return (
    <>
      <sx.ActivityList>
        {recentlyUpdated.map((dataset, index) => (
          <Fragment key={index}>
            {dataset.most_recent_activity_time ? (
              <DatasetActivityCard
                data-testid="dataset-activity-item"
                teamId={dataset.dataset_summary.team_id}
                datasetId={dataset.dataset_summary.id}
                datasetName={dataset.dataset_summary.name}
                updatedTime={dataset.most_recent_activity_time}
                count={dataset.created_count + dataset.updated_count}
              />
            ) : (
              <ActivityTypeCard
                data-testid="dataset-activity-item"
                variant="warning"
              >
                <FormattedMessage id="resources.project.no-activity-card-text" />
              </ActivityTypeCard>
            )}
          </Fragment>
        ))}
      </sx.ActivityList>
      <sx.CountGrid>
        {topFiveDatasetCounts.map((dataset, index) => (
          <Fragment key={index}>
            <DatasetActivityLink
              data-testid="dataset-data-count-item"
              data-fullstoryid="dataset.activity-card.count.link"
              borderColor={colors[index]}
              className="countLink"
              datasetId={dataset.dataset_summary.id}
              datasetName={dataset.dataset_summary.name}
              teamId={dataset.dataset_summary.team_id}
              updateDate={dataset.most_recent_activity_time}
              updateDateTranslationId="Latest Data Update"
            />
            <sx.Count>
              {dataset.terminal_materials_count.toLocaleString()}
            </sx.Count>
          </Fragment>
        ))}
      </sx.CountGrid>

      <MaterialCountPieChart
        colors={colors}
        sortedDatasetCounts={sortedDatasetCounts}
      />
    </>
  );
}

function useDashboardCardData(data?: DatasetMetricResponse) {
  return useMemo(() => {
    if (!data) {
      return {
        sortedDatasetCounts: [],
        topFiveDatasetCounts: [],
        totalMaterials: 0,
        recentlyUpdated: [],
      };
    }
    const counts = Object.values(data.total_counts ?? {}).filter(Boolean);
    const sortedDatasetCounts = orderBy(
      counts,
      (dataset) => dataset.terminal_materials_count,
      "desc",
    );

    const topFiveDatasetCounts = sortedDatasetCounts.length
      ? sortedDatasetCounts.slice(0, 5)
      : [];

    return {
      sortedDatasetCounts,
      topFiveDatasetCounts,
      recentlyUpdated: data.recently_updated ?? [],
    };
  }, [data]);
}
